import { ApiClientWrapper } from '@spike/api-client';
import { NewAppointment } from '@spike/new-booking-model';
import {
    resolveDiscountPayloadForSave,
    resolveOrderItemPriceForSave,
    shouldApplyMembershipRedeemOnSave
} from 'components/CreateBooking/utils/membershipDisplayPrice';
import { Training } from 'model/NewMultipleBooking';
import { resolveBookingType } from 'model/Service';
import { Moment } from 'moment-timezone';
import { buildTrainingVariableDataPerSlot } from 'utils/trainingBulkSlotBuilder';
import { normalizeTrainingForSlotSave } from 'utils/trainingDateSlots';

export interface BuildTrainingAppointmentsParams {
    apiClientWrapper: ApiClientWrapper;
    bookingId: number;
    training: Training;
    petId: number;
    customerId: number;
    createdByStaffId: number;
    fallbackDate: Moment;
    timezone: string;
    status: number;
    marketplaceAddressId: number;
}

export const buildTrainingAppointments = ({
    apiClientWrapper,
    bookingId,
    training,
    petId,
    customerId,
    createdByStaffId,
    fallbackDate,
    timezone,
    status,
    marketplaceAddressId
}: BuildTrainingAppointmentsParams): Array<NewAppointment> => {
    const normalizedTraining = normalizeTrainingForSlotSave(training, timezone);
    const fallbackInTz = fallbackDate.clone().tz(timezone);
    const variableDataPerSlot = buildTrainingVariableDataPerSlot(normalizedTraining, fallbackInTz, timezone, false);

    return variableDataPerSlot.map(variableData => {
        const membershipDiscount = resolveDiscountPayloadForSave(variableData.itemData?.discount, variableData.service);
        const enrollmentId = shouldApplyMembershipRedeemOnSave(variableData.service)
            ? variableData.service?.enrollment?.id
            : undefined;
        const dateToSave = variableData.date!.clone().tz(timezone).seconds(0).milliseconds(0);
        const dateEndToSave = variableData.dateEnd
            ? variableData.dateEnd.clone().tz(timezone).seconds(0).milliseconds(0)
            : dateToSave.clone().add(variableData.service?.durationInMinutes || 0, 'minutes');

        return {
            booking_id: bookingId,
            uuid: apiClientWrapper.uuid(),
            address_id: marketplaceAddressId,
            booked_at: dateToSave,
            booking_end_at: dateEndToSave,
            customer_id: customerId,
            pet_id: petId,
            service_id: variableData.service?.service.id!,
            staff_id: variableData.staffId,
            created_by: createdByStaffId,
            notes: '',
            order_item_price: resolveOrderItemPriceForSave(variableData.service),
            status,
            discount_amount: membershipDiscount?.amount,
            discount_percentage: membershipDiscount?.percentage,
            details: {
                training: {
                    type: variableData.service?.inHomeService ? 'in_home' : 'brick_and_mortar',
                    address: variableData.service?.address || '',
                    duration: variableData.service?.durationInMinutes || 0,
                    instructions: variableData.service?.instructions,
                    sessionType: resolveBookingType(variableData.service?.bookingType)
                }
            },
            service_session_id: variableData.service?.serviceSessionId ?? null,
            ...(enrollmentId != null
                ? {
                      redeemType: 'Enrollment' as const,
                      redeemId: enrollmentId
                  }
                : {})
        };
    });
};
